:root {
  color: #eef7f3;
  background: #081012;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-synthesis: none;
  --page: #081012;
  --panel: #101a1d;
  --panel-light: #162429;
  --line: rgba(229, 245, 239, 0.14);
  --muted: #9bb0ab;
  --mint: #83f1c1;
  --aqua: #62d7e8;
  --yellow: #ffd76a;
}

* {
  box-sizing: border-box;
}

html {
  min-height: 100%;
  background: var(--page);
}

body {
  min-width: 320px;
  min-height: 100vh;
  margin: 0;
  background:
    radial-gradient(circle at 20% 0%, rgba(98, 215, 232, 0.12), transparent 35rem),
    radial-gradient(circle at 100% 70%, rgba(131, 241, 193, 0.08), transparent 32rem),
    var(--page);
}

button,
a {
  font: inherit;
}

a {
  color: inherit;
}

.page {
  width: min(1080px, calc(100% - 32px));
  margin: 0 auto;
}

.topbar {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.82rem;
}

.topbar nav {
  display: flex;
  gap: 8px;
}

.topbar a {
  text-decoration: none;
}

.topbar a:hover {
  color: #ffffff;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #eef7f3;
  font-weight: 700;
}

.brand-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--mint);
  box-shadow: 0 0 16px rgba(131, 241, 193, 0.7);
}

main {
  padding: 56px 0 44px;
}

.game-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.7fr);
  align-items: end;
  gap: 48px;
  margin-bottom: 32px;
}

.level-label {
  margin: 0 0 10px;
  color: var(--mint);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  font-weight: 700;
}

h1 {
  margin: 0;
  font-size: clamp(2.7rem, 8vw, 5.4rem);
  font-weight: 650;
  line-height: 1;
}

.instructions {
  margin: 0;
  color: var(--muted);
  font-size: 1.02rem;
  line-height: 1.65;
}

.game-intro {
  display: grid;
  gap: 18px;
}

.game-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.level-picker {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 11px;
  color: var(--muted);
  font-size: 0.78rem;
}

.level-picker select {
  max-width: 150px;
  border: 0;
  color: #eef7f3;
  background: transparent;
  font: inherit;
  font-weight: 700;
}

.level-picker option {
  color: #eef7f3;
  background: #101b1e;
}

.compact-button {
  min-height: 42px;
  padding: 0 14px;
  color: #dce9e5;
  border-color: var(--line);
  border-radius: 11px;
  background: transparent;
}

.compact-button:hover {
  border-color: rgba(131, 241, 193, 0.45);
  background: rgba(131, 241, 193, 0.05);
}

.game-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 24px;
  background: rgba(16, 26, 29, 0.92);
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.26);
}

.hud {
  min-height: 68px;
  padding: 0 22px;
  display: grid;
  grid-template-columns: 100px 1fr 100px;
  align-items: center;
  border-bottom: 1px solid var(--line);
}

.hud-item {
  display: flex;
  align-items: baseline;
  gap: 9px;
  color: var(--muted);
  font-size: 0.76rem;
}

.hud-item strong {
  color: #ffffff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 1rem;
}

.hud-item-right {
  justify-content: flex-end;
}

.status {
  margin: 0;
  color: #d5e2de;
  text-align: center;
  font-size: 0.9rem;
}

.game-viewport {
  overflow: hidden;
  background: #0b1417;
}

.mobile-pan-hint {
  display: none;
}

#game {
  width: 100%;
  aspect-ratio: 3 / 2;
  min-height: 320px;
  background: #0b1417;
}

#game canvas {
  display: block;
}

.controls {
  min-height: 78px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 13px 18px;
  border-top: 1px solid var(--line);
}

button,
.dialog-actions a {
  min-height: 46px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 700;
  text-decoration: none;
}

button:focus-visible,
a:focus-visible {
  outline: 3px solid rgba(98, 215, 232, 0.55);
  outline-offset: 3px;
}

.primary-button {
  display: inline-flex;
  align-items: center;
  gap: 26px;
  color: #071411;
  background: var(--mint);
}

.primary-button:hover {
  background: #a4f8d3;
}

.primary-button:disabled,
.secondary-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.secondary-button {
  color: #dce9e5;
  border-color: var(--line);
  background: transparent;
}

.secondary-button:hover {
  border-color: rgba(229, 245, 239, 0.3);
  background: rgba(255, 255, 255, 0.04);
}

.keyboard-help {
  margin: 16px 4px 0;
  color: #718681;
  font-size: 0.8rem;
}

.win-dialog {
  width: min(430px, calc(100% - 32px));
  padding: 32px;
  border: 1px solid rgba(131, 241, 193, 0.35);
  border-radius: 22px;
  color: #eef7f3;
  background: #101b1e;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.55);
}

.win-dialog::backdrop {
  background: rgba(2, 8, 9, 0.78);
  backdrop-filter: blur(5px);
}

.win-dialog h2 {
  margin: 0 0 12px;
  font-size: 2rem;
}

.win-dialog p:not(.level-label) {
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
}

.win-icon {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  margin-bottom: 24px;
  border-radius: 15px;
  color: #071411;
  background: var(--mint);
  font-size: 1.4rem;
  font-weight: 900;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 28px;
}

.dialog-actions a {
  display: inline-flex;
  align-items: center;
  color: #dce9e5;
  border-color: var(--line);
}

.dialog-actions button {
  color: #071411;
  background: var(--mint);
}

.dialog-actions .dialog-secondary {
  color: #dce9e5;
  border-color: var(--line);
  background: transparent;
}

.editor-dialog {
  width: min(980px, calc(100% - 28px));
  max-height: min(900px, calc(100vh - 28px));
  padding: 0;
  overflow: auto;
  border: 1px solid rgba(131, 241, 193, 0.28);
  border-radius: 22px;
  color: #eef7f3;
  background: #0e181b;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.62);
}

.editor-dialog::backdrop {
  background: rgba(2, 8, 9, 0.84);
  backdrop-filter: blur(5px);
}

.editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 26px;
  border-bottom: 1px solid var(--line);
}

.editor-header h2 {
  margin: 0;
  font-size: 1.8rem;
}

.editor-header .level-label {
  margin-bottom: 5px;
}

.icon-button {
  width: 42px;
  min-height: 42px;
  padding: 0;
  color: var(--muted);
  border-color: var(--line);
  border-radius: 50%;
  background: transparent;
  font-size: 1.45rem;
}

.icon-button:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.05);
}

.editor-body {
  display: grid;
  grid-template-columns: 245px minmax(0, 1fr);
  gap: 28px;
  padding: 26px;
}

.editor-sidebar {
  display: grid;
  align-content: start;
  gap: 23px;
}

.editor-title-label {
  display: grid;
  gap: 8px;
  color: var(--muted);
  font-size: 0.78rem;
}

.editor-title-label input {
  width: 100%;
  height: 43px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  color: #eef7f3;
  background: #111f22;
  font: inherit;
}

.editor-title-label input:focus {
  border-color: rgba(98, 215, 232, 0.62);
  outline: 0;
}

.editor-section-label {
  margin: 0 0 10px;
  color: var(--muted);
  font-size: 0.78rem;
}

.grid-size-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.grid-size-controls label {
  display: grid;
  gap: 7px;
  color: var(--muted);
  font-size: 0.72rem;
}

.grid-size-controls select {
  height: 40px;
  padding: 0 9px;
  border: 1px solid var(--line);
  border-radius: 9px;
  color: #eef7f3;
  background: #111f22;
  font: inherit;
}

.channel-palette {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
}

.channel-button {
  min-height: 38px;
  padding: 0;
  border-color: var(--line);
  border-radius: 9px;
  color: var(--channel-color);
  background: #101d20;
}

.channel-button::before {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 12px color-mix(in srgb, currentColor 55%, transparent);
  content: "";
}

.channel-button.selected {
  border-color: var(--channel-color);
  background: color-mix(in srgb, var(--channel-color) 16%, #101d20);
}

.tile-palette {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.palette-button {
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  color: #cad9d5;
  border-color: var(--line);
  border-radius: 9px;
  background: #101d20;
  font-size: 0.73rem;
  font-weight: 650;
  text-align: left;
}

.palette-button:hover {
  border-color: rgba(98, 215, 232, 0.4);
}

.palette-button.selected {
  color: #071411;
  border-color: var(--mint);
  background: var(--mint);
}

.palette-symbol {
  width: 20px;
  flex: 0 0 20px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 1rem;
  text-align: center;
}

.fixed-toggle {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #cad9d5;
  font-size: 0.82rem;
}

.fixed-toggle input {
  width: 17px;
  height: 17px;
  accent-color: var(--mint);
}

.editor-tip {
  margin: 0;
  color: #78908a;
  font-size: 0.78rem;
  line-height: 1.55;
}

.editor-workspace {
  min-width: 0;
}

.editor-grid {
  display: grid;
  grid-template-columns: repeat(var(--editor-columns, 6), 1fr);
  gap: 7px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background:
    linear-gradient(rgba(98, 215, 232, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(98, 215, 232, 0.04) 1px, transparent 1px),
    #0a1417;
  background-size: 24px 24px;
}

.editor-cell {
  position: relative;
  aspect-ratio: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(229, 245, 239, 0.11);
  border-radius: 11px;
  color: var(--aqua);
  background: rgba(18, 32, 36, 0.82);
}

.editor-cell:hover {
  border-color: rgba(98, 215, 232, 0.5);
  background: #14252a;
}

.editor-cell svg {
  width: 100%;
  height: 100%;
  display: block;
}

.cell-lock {
  position: absolute;
  top: 5px;
  right: 6px;
  color: var(--yellow);
  font-size: 0.65rem;
}

.editor-status {
  min-height: 20px;
  margin: 12px 2px 0;
  color: var(--muted);
  font-size: 0.8rem;
}

.editor-status.error {
  color: #ff9c87;
}

.level-data {
  margin: 0 26px;
  padding: 14px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 0.8rem;
}

.level-data summary {
  cursor: pointer;
}

.level-data textarea {
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  resize: vertical;
  border: 1px solid var(--line);
  border-radius: 9px;
  color: #b9d8d0;
  background: #071113;
  font: 0.72rem/1.5 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.data-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.data-actions button {
  min-height: 38px;
}

.editor-actions {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 18px 26px 24px;
}

.editor-action-spacer {
  flex: 1;
}

@media (max-width: 700px) {
  .page {
    width: min(100% - 20px, 1080px);
  }

  .topbar {
    min-height: 62px;
  }

  .topbar nav {
    display: none;
  }

  main {
    padding-top: 36px;
  }

  .game-heading {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 22px;
  }

  .instructions {
    font-size: 0.94rem;
  }

  .game-tools {
    justify-content: space-between;
  }

  .level-picker {
    flex: 1;
    justify-content: space-between;
  }

  .game-panel {
    border-radius: 18px;
  }

  .hud {
    min-height: 60px;
    grid-template-columns: 70px 1fr 70px;
    padding: 0 14px;
  }

  .hud-item {
    display: grid;
    gap: 1px;
  }

  .hud-item-right {
    justify-items: end;
  }

  .status {
    font-size: 0.75rem;
  }

  #game {
    width: 600px;
    min-width: 600px;
    min-height: 0;
  }

  .game-viewport {
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-color: rgba(131, 241, 193, 0.35) transparent;
  }

  .mobile-pan-hint {
    display: block;
    margin: 0;
    padding: 9px 14px;
    border-bottom: 1px solid var(--line);
    color: #78908a;
    background: #0b1417;
    font-size: 0.72rem;
  }

  .controls {
    min-height: 70px;
  }

  .primary-button {
    flex: 1;
    justify-content: space-between;
  }

  .dialog-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .dialog-actions a,
  .dialog-actions button {
    justify-content: center;
  }

  .dialog-actions #next-level {
    grid-column: 1 / -1;
  }

  .editor-dialog {
    width: calc(100% - 14px);
    max-height: calc(100vh - 14px);
    border-radius: 16px;
  }

  .editor-header {
    padding: 18px;
  }

  .editor-body {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 18px;
  }

  .editor-sidebar {
    gap: 16px;
  }

  .tile-palette {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .palette-button {
    justify-content: center;
    padding: 0 6px;
  }

  .palette-symbol {
    display: none;
  }

  .editor-grid {
    gap: 4px;
    padding: 7px;
  }

  .editor-cell {
    border-radius: 7px;
  }

  .level-data {
    margin: 0 18px;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 16px 18px 20px;
  }

  .editor-action-spacer {
    display: none;
  }

  .editor-actions button {
    flex: 1 1 calc(50% - 9px);
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition: none !important;
  }
}
